import { Controller } from '@hotwired/stimulus';
import { loadScript } from '@paypal/paypal-js';
import { patch } from '@rails/request.js';

// this controller is especially for the new booking process and kind of a copy of the old one
export default class extends Controller {
  static values = {
    clientId: String,
    locale: String,
    amount: String,
    onCancelUrl: String,
    onErrorUrl: String,
    prepareUrl: String,
    completeUrl: String,
    handlePaidUrl: String,
    bookingNumber: String,
    // Copy comes from the server so it is localized; ES modules cannot reach the Rails translations.
    confirmFirstText: String,
    loadFailedText: String,
    paymentFailedText: String
  };

  connect() {
    this.paypalRendered = false;
    this.confirmCheckbox = document.querySelector(
      'input[name="form[confirmed]"][type="checkbox"]'
    );
    this.boundOnConfirmChange = this.onConfirmChange.bind(this);

    if (this.confirmCheckbox) {
      this.confirmCheckbox.addEventListener('change', this.boundOnConfirmChange);
    }

    if (this.confirmCheckbox?.checked) {
      this.renderPaypal();
    } else {
      this.showPendingConfirmation();
    }
  }

  disconnect() {
    if (this.confirmCheckbox && this.boundOnConfirmChange) {
      this.confirmCheckbox.removeEventListener('change', this.boundOnConfirmChange);
    }
  }

  onConfirmChange(event) {
    if (event.target.checked) {
      if (!this.paypalRendered) {
        this.element.innerHTML = '';
        this.renderPaypal();
      }
    } else {
      // AGB unchecked again → tear the PayPal buttons back down and re-prompt for confirmation.
      this.element.innerHTML = '';
      this.paypalRendered = false;
      this.showPendingConfirmation();
    }
  }

  showPendingConfirmation() {
    this.element.replaceChildren(this.hint(this.confirmFirstTextValue));
  }

  // Built as a node with textContent, so a translation can never inject markup.
  hint(text, className = 'booking-form__hint') {
    const hint = document.createElement('p');
    hint.className = className;
    hint.textContent = text;
    return hint;
  }

  // A failure the customer has to see: the SDK never loaded, or a capture died mid-flow. The first has no
  // buttons to keep; the second must not touch them — the live PayPal iframe would not survive its own
  // markup being re-parsed, so the message is inserted alongside rather than written over the top. Repeated
  // errors replace the message instead of stacking.
  showError(text, { keepButtons = false } = {}) {
    const message = this.hint(text, 'booking-form__hint booking-form__hint--error');

    if (!keepButtons) {
      this.element.replaceChildren(message);
      return;
    }

    this.element.querySelector('.booking-form__hint--error')?.remove();
    this.element.prepend(message);
  }

  async renderPaypal() {
    let locale;
    switch (this.localeValue) {
      case 'de':
        locale = 'de_DE';
        break;
      case 'en':
        locale = 'en_US';
        break;
    }

    let paypal;
    try {
      paypal = await loadScript({
        'client-id': this.clientIdValue,
        currency: 'EUR',
        intent: 'capture',
        locale: locale
      });
    } catch (error) {
      console.error('failed to load the PayPal JS SDK script', error);
    }

    if (!paypal) {
      this.showError(this.loadFailedTextValue);
      return;
    }

    try {
      await paypal
        .Buttons({
          createOrder: async (data, actions) => {
            let orderId = await actions.order.create({
              purchase_units: [
                {
                  reference_id: this.bookingNumberValue,
                  custom_id: this.bookingNumberValue,
                  amount: {
                    value: this.amountValue
                  }
                }
              ]
            });

            const response = await this.handlePatch(
              this.prepareUrlValue,
              { paypal_order_id: orderId },
              'application/json'
            );

            localStorage.setItem('orderEventId', response.event_id);

            return orderId;
          },
          onCancel: async () => {
            await this.handlePatch(
              this.onCancelUrlValue,
              { event_id: this.eventId() },
              'application/json'
            );

            return true;
          },
          // The failed endpoint answers 200, so nothing reloads the page here — say so on the spot, or
          // the customer is left looking at buttons that already gave up.
          onError: async () => {
            await this.handlePatch(
              this.onErrorUrlValue,
              { event_id: this.eventId() },
              'application/json'
            );

            this.showError(this.paymentFailedTextValue, { keepButtons: true });
          },
          onApprove: async (data, actions) => {
            const completeResponse = await this.handlePatch(
              this.completeUrlValue,
              { paypal_order_id: data.orderID, event_id: this.eventId() },
              'application/json'
            );

            if (!completeResponse.success) throw 'Payment not completed';

            window.location.href = this.handlePaidUrlValue;
          }
        })
        .render(`#${this.element.id}`);

      this.paypalRendered = true;
    } catch (error) {
      console.error('failed to render the PayPal Buttons', error);
      this.showError(this.loadFailedTextValue);
    }
  }

  handlePatch = async (url, body, responseKind) => {
    const response = await patch(url, { body, responseKind });

    if (response.statusCode == 409) {
      location.reload();
    }

    if (response.ok && responseKind === 'application/json') {
      return await response.json;
    }
  };

  eventId = () => {
    return localStorage.getItem('orderEventId');
  };
}
